/**
 * Header providers — small helpers that produce request headers
 * conditionally. The client calls each provider on every request and
 * merges the returned object into the final header set. Returning
 * `null` / `undefined` / `{}` is fine (skipped).
 *
 * Providers are functions, not static objects, so a single client
 * instance can follow mutable state (current org, current sub-tenant,
 * current bearer token) without any observer wiring. Apps usually hold
 * the mutable state in a module-level variable that a setter updates.
 *
 *     let orgSlug = null;
 *     export const setOrgSlug = (s) => { orgSlug = s; };
 *     const orgHeader = headerFromGetter(() => orgSlug, "X-Organization");
 *
 *     const client = createClient({
 *       baseUrl: "/api",
 *       headers: [orgHeader],
 *     });
 */

/**
 * Build a provider from a getter returning either a string or `null`.
 * Emits `{ [name]: value }` when the getter returns a truthy value;
 * omits the header otherwise.
 *
 * @param {() => string | null | undefined} getter
 * @param {string} name
 * @returns {() => Record<string, string>}
 */
export function headerFromGetter(getter, name) {
  return () => {
    const v = getter();
    return v ? { [name]: String(v) } : {};
  };
}

/**
 * Convenience: a provider that always sets `Authorization: Bearer <token>`
 * when the getter yields a non-empty token.
 *
 * @param {() => string | null | undefined} getter
 */
export function bearerAuth(getter) {
  return () => {
    const t = getter();
    return t ? { Authorization: `Bearer ${t}` } : {};
  };
}

/**
 * Merge headers from multiple providers. Later providers win on
 * collisions. Undefined/null values are dropped.
 *
 * @param {Array<() => Record<string, string> | null | undefined>} providers
 * @returns {Record<string, string>}
 */
export function mergeHeaderProviders(providers) {
  const out = {};
  for (const p of providers || []) {
    const h = p?.();
    if (!h) continue;
    for (const [k, v] of Object.entries(h)) {
      if (v != null) out[k] = String(v);
    }
  }
  return out;
}
